import { Grid } from '@mui/material';
import { get, isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import type { Path } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { ResourceSelectorSidebar } from 'src/components/widget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { useModal } from 'src/hooks';
import { lang } from 'src/utils';
import { GcfMockData } from 'src/utils/gcf-helper';

import {
  type ConfirmationHeaderConfigInterfaceUS,
  BannerImageType,
} from '../CODForm.interface';
import { MessagingTitle } from '../messagingTitle';
import { HeaderSectionProps } from './HeaderSection.interface';
import { CustomImageSelectionButton } from './modules/CustomImageSelectionButton';

export const HeaderSection = (props: HeaderSectionProps) => {
  const openResourceDrawer = useModal();

  const [selectedHeader, setSelectedHeader] = useState<'FULL' | 'SHORT'>(
    'FULL',
  );
  //Setting the preview based on initial header selected
  useEffect(() => {
    props.setOrderType(
      selectedHeader === 'FULL'
        ? GcfMockData.COD_CUSTOMER_KNOWN
        : GcfMockData.COD_EXPANDED_WITH_SMALL_BANNER,
    );
  });
  const handleSelectedHeader =
    (header: 'FULL' | 'SHORT') => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      setSelectedHeader(header);

      props.setOrderType(
        header === 'FULL'
          ? GcfMockData.COD_CUSTOMER_KNOWN
          : GcfMockData.COD_EXPANDED_WITH_SMALL_BANNER,
      );
    };

  const handleFieldChange =
    (field: Path<ConfirmationHeaderConfigInterfaceUS>) => (value: any) => {
      props.handleOnChange(`codSettings.confirmSetting.header.${field}`, value);
    };

  const confirmSettingLargeImage = get(
    props.header,
    `review.banner.images.${BannerImageType.LARGE_IMAGE}.background`,
  );

  const confirmSettingSmallImage = get(
    props.header,
    `review.banner.images.${BannerImageType.SMALL_IMAGE}.background`,
  );

  const customMessageItems = [
    {
      titleComponent: (
        <MessagingTitle
          label={lang.fullHeader}
          hasError={
            props.showValidationErrors &&
            (!!props.errors?.review?.banner?.images?.largeImage?.background ||
              !props.hasCodFullBanner)
          }
          isHighlighted={selectedHeader === 'FULL'}
          onClick={handleSelectedHeader('FULL')}
        />
      ),
      hasError: props.showValidationErrors && !props.hasCodFullBanner,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <CustomImageSelectionButton
              showButtonOnly={isEmpty(confirmSettingLargeImage)}
              assetName={confirmSettingLargeImage}
              handleClick={() => {
                openResourceDrawer.onOpenModal((value: any) => {
                  handleFieldChange(
                    `review.banner.images.${BannerImageType.LARGE_IMAGE}.background`,
                  )(value);
                });
              }}
              handleRemoveImage={() => {
                handleFieldChange(
                  `review.banner.images.${BannerImageType.LARGE_IMAGE}.background`,
                )('');
              }}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      titleComponent: (
        <MessagingTitle
          label={lang.reducedHeader}
          hasError={
            props.showValidationErrors &&
            (!!props.errors?.review?.banner?.images?.smallImage?.background ||
              !props.hasCodHalfBanner)
          }
          isHighlighted={selectedHeader === 'SHORT'}
          onClick={handleSelectedHeader('SHORT')}
        />
      ),
      hasError: props.showValidationErrors && !props.hasCodHalfBanner,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <CustomImageSelectionButton
              showButtonOnly={isEmpty(confirmSettingSmallImage)}
              assetName={confirmSettingSmallImage}
              handleClick={() => {
                openResourceDrawer.onOpenModal((value: any) => {
                  handleFieldChange(
                    `review.banner.images.${BannerImageType.SMALL_IMAGE}.background`,
                  )(value);
                });
              }}
              handleRemoveImage={() => {
                handleFieldChange(
                  `review.banner.images.${BannerImageType.SMALL_IMAGE}.background`,
                )('');
              }}
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <>
      <SidebarAccordion items={customMessageItems} />

      <ResourceSelectorSidebar
        label=""
        type={ResourceType.BACKGROUND}
        openResourceSelectorDrawer={openResourceDrawer.open}
        onToggleResourceSelectorDrawer={openResourceDrawer.onToggleModal}
        onChange={openResourceDrawer.customFn}
      />
    </>
  );
};
